import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatCurrency, formatDate } from "../../../core/common/api";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";
import ResetButton from "../../../shared/ResetButton";
import useTableSearch from "../../../shared/useTableSearch";

const WithdrawalList = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const appliedSearch = useTableSearch(search);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });

  const loadRows = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pagination.limit),
          status,
        });
        if (appliedSearch.trim()) params.set("search", appliedSearch.trim());
        const res = await apiRequest(`/withdrawals?${params}`, "GET");
        setRows(res.withdrawals || []);
        setStats(res.stats || {});
        setPagination({
          page: res.pagination?.page || page,
          limit: res.pagination?.limit || pagination.limit,
          totalPages: res.pagination?.totalPages || 1,
          total: res.pagination?.total || 0,
        });
      } catch (error: any) {
        Swal.fire("Error", error.message || "Unable to load withdrawals", "error");
      } finally {
        setIsLoading(false);
      }
    },
    [pagination.limit, appliedSearch, status],
  );

  useEffect(() => {
    void loadRows(1);
  }, [loadRows]);

  const updateStatus = async (id: string, nextStatus: string) => {
    const { value: remark } = await Swal.fire({
      title: `${nextStatus} withdrawal?`,
      input: "text",
      inputPlaceholder: "Remark (optional)",
      showCancelButton: true,
      confirmButtonText: nextStatus,
    });
    if (remark === undefined) return;

    try {
      await apiRequest(`/withdrawals/${id}`, "PUT", { status: nextStatus, remark });
      Swal.fire({
        title: "Updated",
        icon: "success",
        timer: 1000,
        showConfirmButton: false,
      });
      void loadRows(pagination.page);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to update withdrawal", "error");
    }
  };

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MLM</div>
          <h1 className="page-title">
            Withdrawal <em>Requests</em>
          </h1>
          <div className="page-sub">
            Withdraw from eWallet. Reject refunds the amount back to eWallet.
          </div>
        </div>
      </div>

      <section className="metrics-grid">
        <MetricCard
          metric={{
            label: "Pending",
            value: stats.pending || 0,
            icon: "⏳",
            tone: "orange",
          }}
        />
        <MetricCard
          metric={{
            label: "In this list",
            value: pagination.total,
            icon: "📄",
            tone: "blue",
          }}
        />
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value.toUpperCase())}
            placeholder="Search member ID"
          />
        </div>
        <select
          className="filter-select"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">All status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="rejected">Rejected</option>
        </select>

        <ResetButton
          onClick={() => {
            setSearch("");
            setStatus("pending");
          }}
        />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Requests ({pagination.total})</h2>
          <p>Reject refunds the amount back to the member wallet</p>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Member</th>
                <th>Fund</th>
                <th>Amount</th>
                <th>Charges</th>
                <th>Net pay</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    Loading requests...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    No withdrawal requests.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <strong>{row.memberId}</strong>
                      <div className="table-muted">{row.user?.name || "—"}</div>
                    </td>
                    <td>{row.fundLabel || row.fund}</td>
                    <td>{formatCurrency(row.amount)}</td>
                    <td>
                      Admin {formatCurrency(row.adminCharge)}
                      {row.tds ? ` · TDS ${formatCurrency(row.tds)}` : ""}
                    </td>
                    <td>{formatCurrency(row.netAmount)}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          row.status === "paid" || row.status === "approved"
                            ? "success"
                            : row.status === "rejected"
                              ? "danger"
                              : "warning"
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      <div className="actions-btns">
                        {row.user?._id && (
                          <Link to={`/users/${row.user._id}`} className="btn-sm btn-outline">
                            User
                          </Link>
                        )}
                        {row.status === "pending" && (
                          <>
                            <button
                              type="button"
                              className="btn-sm btn-primary"
                              onClick={() => void updateStatus(row._id, "approved")}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="btn-sm btn-danger"
                              onClick={() => void updateStatus(row._id, "rejected")}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {row.status === "approved" && (
                          <button
                            type="button"
                            className="btn-sm btn-primary"
                            onClick={() => void updateStatus(row._id, "paid")}
                          >
                            Mark paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="requests"
          onPageChange={(page) => void loadRows(page)}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, limit, page: 1 }))
          }
        />
      </section>
    </>
  );
};

export default WithdrawalList;
